// ══════════════════════════════════════════════════════════════
// COMPASS – Per-feature translation loader
// ══════════════════════════════════════════════════════════════
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TranslateLoader, TranslationObject, mergeDeep } from '@ngx-translate/core';
import { Observable, catchError, forkJoin, map, of } from 'rxjs';
import { Logger } from '../services/logger.service';
import { TRANSLATIONS_PATH, TRANSLATION_FILES } from './languages.config';

const MISSING = Symbol('missing translation file');

/**
 * Loads every file in TRANSLATION_FILES for a language and deep-merges them into one
 * translation object. A missing file counts as empty (its keys fall back to the default
 * language), so a language can be translated file by file. Errors only when no file
 * could be loaded, so LanguageService can fall back to the default language.
 */
@Injectable()
export class FeatureTranslateLoader extends TranslateLoader {
  private readonly http = inject(HttpClient);
  private readonly logger = inject(Logger);

  getTranslation(lang: string): Observable<TranslationObject> {
    const files = TRANSLATION_FILES.map(file => {
      const url = `${TRANSLATIONS_PATH}/${lang}/${file}.json`;
      return this.http.get<TranslationObject>(url).pipe(
        catchError(error => {
          this.logger.warn(`Translation file not loaded: ${url}`, error);
          return of(MISSING);
        }),
      );
    });

    return forkJoin(files).pipe(
      map(parts => parts.filter((part): part is TranslationObject => part !== MISSING)),
      map(loaded => {
        if (loaded.length === 0) {
          throw new Error(`No translation files could be loaded for ${lang}`);
        }
        return loaded.reduce<TranslationObject>((merged, part) => mergeDeep(merged, part), {});
      }),
    );
  }
}
